import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { imprimirEtiquetasQr, baixarPdfEtiquetasQr } from '../lib/imprimirEtiquetasQr';

export type ItemQRLote = {
  id: number | string;
  titulo: string;
  subtitulo?: string;
  value: string;
};

type Props = {
  itens: ItemQRLote[];
  onClose: () => void;
};

const sizeOptions: Array<{ id: 'sm' | 'md' | 'lg'; label: string; desc: string; qr: number; mmW: number; mmH: number }> = [
  { id: 'sm', label: 'Pequeno', desc: '50 x 50 mm', qr: 132, mmW: 50, mmH: 50 },
  { id: 'md', label: 'Médio', desc: '72 x 95 mm', qr: 188, mmW: 72, mmH: 95 },
  { id: 'lg', label: 'Grande', desc: '90 x 120 mm', qr: 244, mmW: 90, mmH: 120 },
];

export default function QRPrintModalLote({ itens, onClose }: Props) {
  const [size, setSize] = useState<'sm' | 'md' | 'lg'>('md');
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [erroPdf, setErroPdf] = useState('');
  const qrWrapRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const selected = sizeOptions.find((item) => item.id === size) || sizeOptions[1];

  function coletarEtiquetas() {
    return itens
      .map((item) => {
        const canvas = qrWrapRefs.current[String(item.id)]?.querySelector('canvas') || null;
        if (!canvas) return null;
        return { titulo: item.titulo, subtitulo: item.subtitulo, dataUrl: canvas.toDataURL('image/png') };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }

  async function baixarPdf() {
    if (!itens.length) return;
    setErroPdf('');
    setProgresso(0);
    setGerandoPdf(true);
    try {
      await baixarPdfEtiquetasQr(coletarEtiquetas(), size, setProgresso);
    } catch (e) {
      setErroPdf(e instanceof Error ? e.message : 'Não foi possível gerar o PDF. Tente novamente.');
    } finally {
      setGerandoPdf(false);
      setProgresso(0);
    }
  }

  function imprimirTudo() {
    if (!itens.length) return;
    imprimirEtiquetasQr(coletarEtiquetas(), size);
  }

  return createPortal(
    <div className="modal-bg qr-screen-bg" onClick={onClose}>
      <div className="modal qr-screen-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qr-screen-shell">
          <div className="qr-screen-side noprint">
            <div className="qr-label-kicker">Etiquetas para impressão</div>
            <h3>Emitir QR Code de todos os equipamentos</h3>
            <p>
              {itens.length
                ? `${itens.length} equipamento${itens.length === 1 ? '' : 's'} com QR Code público habilitado.`
                : 'Nenhum equipamento com QR Code público disponível.'}
            </p>
            <div className="qr-size-block">
              <div className="qr-size-title">Tamanho do QR Code</div>
              <div className="qr-size-list">
                {sizeOptions.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`qr-size-option${size === item.id ? ' on' : ''}`}
                    onClick={() => setSize(item.id)}
                  >
                    <strong>{item.label}</strong>
                    <span>{item.desc}</span>
                  </button>
                ))}
              </div>
            </div>
            {erroPdf && <div className="msg-erro">{erroPdf}</div>}
            <div className="qr-screen-actions">
              <button className="btn sec" onClick={onClose}>Cancelar</button>
              <button className="btn sec" disabled={!itens.length || gerandoPdf} onClick={baixarPdf}>
                {gerandoPdf ? `Gerando PDF... ${progresso}/${itens.length}` : 'Baixar PDF'}
              </button>
              <button className="btn" disabled={!itens.length} onClick={imprimirTudo}>Imprimir todas as etiquetas</button>
            </div>
          </div>

          <div className="qr-screen-stage">
            <div className="qr-sticker-lote">
              {itens.map((item) => (
                <div key={item.id} className={`qr-sticker size-${size}`}>
                  <div className="qr-sticker-title">{item.titulo}</div>
                  {item.subtitulo && <div className="qr-sticker-subtitle">{item.subtitulo}</div>}
                  <div className="qr-sticker-qr" ref={(el) => { qrWrapRefs.current[String(item.id)] = el; }}>
                    <QRCodeCanvas value={item.value} size={selected.qr} includeMargin />
                  </div>
                  <div className="qr-sticker-brand subtle">Manux Service</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
